import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    Typography,
    Box,
} from "@mui/material";
import { PaymentManagement } from "./PaymentManagement";
import { Order, PaymentMethod } from "../../types";
import { paymentService } from "../../services/paymentService";

interface PaymentUpdateDialogProps {
    open: boolean;
    onClose: () => void;
    order: Order | null;
    onSuccess: () => void;
    /** Methods offered when adding/editing a payment. Defaults to every
     * order payment method (includes COD) — pass POS_PAYMENT_METHODS when
     * used from an in-person checkout context. */
    paymentMethods?: PaymentMethod[];
}

export const PaymentUpdateDialog: React.FC<PaymentUpdateDialogProps> = ({
    open,
    onClose,
    order,
    onSuccess,
    paymentMethods,
}) => {
    const [payments, setPayments] = useState<
        Array<{
            amount: number;
            payment_method: PaymentMethod;
            reference_number?: string;
            notes?: string;
            account_id?: string | null;
            // Saved payments keep their id, so saving touches only what changed.
            transaction_id?: string;
        }>
    >([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        if (!open || !order) return;

        if (
            order.payment_transactions &&
            Array.isArray(order.payment_transactions) &&
            order.payment_transactions.length > 0
        ) {
            const existingPayments = order.payment_transactions.map(
                (pt: any) => ({
                    transaction_id: pt.transaction_id,
                    amount: pt.amount,
                    payment_method: pt.payment_method as PaymentMethod,
                    reference_number: pt.reference_number,
                    notes: pt.notes,
                    account_id: pt.account_id,
                }),
            );
            setPayments(existingPayments);
        } else {
            setPayments([]);
        }
        setError("");
    }, [open, order]);

    const totalAmount = useMemo(() => {
        if (!order) return 0;
        return Number(order.total_amount) || 0;
    }, [order]);

    // paymentService.syncOrderPayments only touches payments that changed
    // (edited ones in place, keeping their date; removed ones deleted) — but
    // a real bKash-gateway payment can't be deleted or have its money
    // details changed (the backend rejects both, see PaymentTransactionService),
    // so saving could fail with a raw error; better to explain up front and
    // point staff at the right place.
    const hasBkashPayment = useMemo(
        () => (order?.payment_transactions || []).some((pt) => pt.bkash_payment_id),
        [order],
    );

    const handleSave = async () => {
        if (!order || hasBkashPayment) return;

        try {
            setLoading(true);
            setError("");
            await paymentService.syncOrderPayments(order.order_id, payments);
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(
                err.response?.data?.detail ||
                    // A check made before anything was sent (no response).
                    (!err.response && err.message) ||
                    "Failed to update payment details",
            );
        } finally {
            setLoading(false);
        }
    };

    if (!order) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box>
                    <Typography variant="h6">Update Payment</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Order #{order.order_number}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {hasBkashPayment && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This order has a payment made through bKash, which
                        can't be edited here. To refund it, use the Refund
                        action on the payment in Order Details instead —
                        editing here only covers manually entered payments.
                    </Alert>
                )}
                <PaymentManagement
                    payments={payments}
                    totalAmount={totalAmount}
                    onChange={setPayments}
                    disabled={loading || hasBkashPayment}
                    paymentMethods={paymentMethods}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {hasBkashPayment ? "Close" : "Cancel"}
                </Button>
                {!hasBkashPayment && (
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading ? "Saving..." : "Save Payments"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
